// @flow
import type { SubScenario, Condition } from 'types/trident-campaigns';
import React, { Component } from 'react';
import _ from 'lodash';
import i18n from 'i18n-js';
import type { City, Country } from 'types/country';
import FormInputWithError from 'components/form/FormInputWithError';
import DynamicHeightFormSelect from 'components/form/DynamicHeightFormSelect';
import { Segment, Grid, Form, Divider, Header, Label } from 'semantic-grab-ui/src';
import RuleBuilder from 'omega-rtc/components/rule-builder/RuleBuilder';
import RuleBuilderConfig from './TreatmentRuleBuilderConfig';
import EventDropdown from './EventDropdown';
import formComponent from 'components/decorators/form_component';
import { OLD_BOOKING_EVENT_TYPES } from 'data/trident-campaigns';
import './TreatmentForm.scss';
import { getCityOptions, getConditionsFlag, getEventTypesList, getTaxyTypeOptions } from 'utils/new-trident-utils';

type Props = {
  model: SubScenario,
  targetUserType: string,
  cities: Array<City>,
  country: Country,
  scenarioIndex: number,
  onModelChange: (_?: SubScenario) => void,
  handleInputChange: Function,
  handleControlBlur: Function,
  isEdit: boolean,
  error: any,
  listRuleTypes: Object,
};

// SubScenarioForm is the form for one single scenario
@formComponent()
export default class SubScenarioForm extends Component {
  props: Props;

  handleClearCities = () => {
    this.props.onModelChange({
      ...this.props.model,
      pickupCities: [],
      taxiTypeIDs: [],
      allCitiesSelected: false,
      allTaxiTypesSelected: false,
    });
  };

  handleClearTaxiTypeIDs = () => {
    this.props.onModelChange({
      ...this.props.model,
      taxiTypeIDs: [],
      allTaxiTypesSelected: false,
    });
  };

  handleAllCitiesSelected = () => {
    this.props.onModelChange({
      ...this.props.model,
      pickupCities: _.map(this.props.cities, 'cityID'),
      allCitiesSelected: true,
    });
  };

  handleAllTaxiTypesSelected = (taxiTypes: any) => {
    this.props.onModelChange({
      ...this.props.model,
      taxiTypeIDs: _.map(taxiTypes, 'value'),
      allTaxiTypesSelected: true,
    });
  };

  handlePickUpCitiesChange = (event: any, data: any) => {
    this.props.onModelChange({
      ...this.props.model,
      pickupCities: data.value,
      allCitiesSelected: false,
    });
  };

  handleTaxiTypeIdsChange = (event: any, data: any) => {
    this.props.onModelChange({
      ...this.props.model,
      taxiTypeIDs: data.value,
      allTaxiTypesSelected: false,
    });
  };

  handleEventSelection = (event: any, data: any, eventTypeData: any) => {
    if (this.props.isEdit) {
      alert('You cannot change event type when there are other nodes following the scenario');
      return;
    }

    if (this.props.model.eventType === data.value) {
      return;
    }
    const group = eventTypeData.group;
    const noun = eventTypeData.noun;

    // whether we need city ID selection box for this event type
    const { needCityID, needServiceTypeID, needCurrency } = getConditionsFlag(eventTypeData.ruleTypes);
    this.props.onModelChange({
      ...this.props.model,
      eventType: data.value,
      name: data.value,
      group,
      noun,
      condition: null,
      needCityID,
      needServiceTypeID,
      currency: needCurrency ? this.props.country.currency : '',
      needCurrency,
    });
  };

  handleInputChange = (event: any, data: any) => {
    this.props.onModelChange({
      ...this.props.model,
      [data.name]: data.value,
    });
  };

  render() {
    const { model, listRuleTypes, country, targetUserType } = this.props;
    const subScenario = model;

    // Mandatory Conditions Dropdown
    const citiesOptions = getCityOptions(this.props.cities, this.handleAllCitiesSelected);
    const taxiTypeIDsOptions = getTaxyTypeOptions(this.props.cities, _.get(subScenario, 'pickupCities', []), this.handleAllTaxiTypesSelected);

    const isOldBookingEvents = _.includes(OLD_BOOKING_EVENT_TYPES, _.get(subScenario, 'eventType', ''));

    // Mandatory Conditions Flag
    const needCityID = _.get(subScenario, 'needCityID', false) || isOldBookingEvents;
    const needServiceTypeID = _.get(subScenario, 'needServiceTypeID', false) || isOldBookingEvents;
    const needCurrencyCondition = _.get(subScenario, 'needCurrency', false);

    const eventType = subScenario.eventType;
    const eventName = _.get(subScenario, 'noun', eventType);
    const eventTypeData = _.get(listRuleTypes, targetUserType, []).find(
      eventData => _.get(eventData, 'eventType', '') === eventType,
    );
    const ruleTypes = _.get(eventTypeData, 'ruleTypes', []).filter(
      ruleType => _.get(ruleType, 'displayType', '') !== 'limit',
    );
    const eventDescription = _.get(eventTypeData, 'description', '');

    const getDefaultCondition = (condition: Condition) => {
      if (_.isEmpty(condition) || _.isEmpty(_.get(condition, 'operator'))) {
        return null;
      }
      return condition;
    };

    return (
      <Segment className="treatment-form-segment">
        {/* Events */}
        <Grid>
          <Grid.Column width={12}>
            <Form>
              <Grid>
                <Grid.Column width={12}>
                  <FormInputWithError
                    name="name"
                    {...this.props}
                  >
                    <Form.Input
                      label="Scenario Name"
                      value={_.get(subScenario, 'name', '')}
                      onChange={this.handleInputChange}
                    />
                  </FormInputWithError>
                </Grid.Column>
              </Grid>
            </Form>
          </Grid.Column>
        </Grid>
        <Grid>
          <Grid.Column width={12}>
            <Form>
              <Grid>
                <Grid.Column width={12}>
                  <Label
                    size="small"
                    color="green"
                  >
                    Event Type
                  </Label>
                  <EventDropdown
                    eventTypesObject={getEventTypesList(listRuleTypes, targetUserType)} selectedEventName={eventName}
                    handleEventSelection={this.handleEventSelection}
                  />
                </Grid.Column>
              </Grid>
            </Form>
          </Grid.Column>
        </Grid>
        {!_.isEmpty(eventDescription) && (
          <Grid.Column width={12}>
            <br />
            <p>{eventDescription}</p>
          </Grid.Column>
        )}
        {/* Conditions */}
        <Divider className="wider" />

        <Grid>
          <Grid.Column width={4}>
            <Header as="h3">{i18n.t('targets_page.form.treatment_conditions')}</Header>
          </Grid.Column>
        </Grid>
        <br />
        <br />
        {(needCityID || needServiceTypeID) &&
        <Grid>
          <Grid.Column width={16}>
            <Form>
              <Grid>
                {needCityID &&
                <Grid.Column width={8}>
                  <Form.Field className="promotions-cities-select-wrapper required">
                    <label>{i18n.t('promotions_page.fields.city')}</label>
                    <span className="cities-select-wrapper__count">
                      ({_.size(_.get(subScenario, 'pickupCities', []))} Selected)
                    </span>
                    <a
                      className="cities-select-wrapper__clear-all"
                      onClick={this.handleClearCities}
                    >
                      Clear all
                    </a>
                    <FormInputWithError
                      name={'pickupCities'}
                      {...this.props}
                    >
                      <DynamicHeightFormSelect
                        multiple
                        options={citiesOptions}
                        value={_.get(subScenario, 'allCitiesSelected') ? [0] : _.get(subScenario, 'pickupCities', [])}
                        onChange={this.handlePickUpCitiesChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Form.Field>
                </Grid.Column>
                }

                {needServiceTypeID && <Grid.Column width={8}>
                  <Form.Field className="promotions-taxi-types-ids-wrapper required">
                    <label>{i18n.t('promotions_page.fields.eligibility.taxi_types_ids')}</label>
                    <span className="taxi-types-ids-select-wrapper__count">
                      ({_.size(_.get(subScenario, 'taxiTypeIDs', []))} Selected)
                    </span>
                    <a
                      className="taxi-types-ids-select-wrapper__clear-all"
                      onClick={this.handleClearTaxiTypeIDs}
                    >
                      Clear all
                    </a>
                    <FormInputWithError
                      name={'taxiTypeIDs'}
                      {...this.props}
                    >
                      <DynamicHeightFormSelect
                        id="taxi-types-ids-tags"
                        search
                        multiple
                        selection
                        allowAdditions
                        options={taxiTypeIDsOptions}
                        value={_.get(subScenario, 'allTaxiTypesSelected') ? [0] : _.get(subScenario, 'taxiTypeIDs', [])}
                        onChange={this.handleTaxiTypeIdsChange}
                        onBlur={this.props.handleControlBlur}
                      />
                    </FormInputWithError>
                  </Form.Field>
                </Grid.Column>
                }

              </Grid>
            </Form>
          </Grid.Column>
        </Grid>
        }
        {needCurrencyCondition &&
        <Grid>
          <Grid.Column width={8}>
            <Form>
              <Grid>
                <Grid.Column width={8}>
                  <Form.Field>
                    <FormInputWithError
                      name={'currency'}
                      {...this.props}
                    >
                      <Form.Input
                        required
                        label={i18n.t('targets_page.fields.currency')}
                        value={country && country.currency}
                        onChange={this.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                        disabled
                      />
                    </FormInputWithError>
                  </Form.Field>
                </Grid.Column>
              </Grid>
            </Form>
          </Grid.Column>
        </Grid>
        }
        <Grid>
          <Grid.Column width={16}>
            {!_.isEmpty(_.get(subScenario, 'eventType')) && (
              <FormInputWithError
                name="condition"
                {...this.props}
              >
                <RuleBuilder
                  key={`rule-builder-${subScenario.eventType}`}
                  defaultValue={getDefaultCondition(subScenario.condition)}
                  onChange={(rule) => this.handleInputChange({}, { name: 'condition', value: rule })}
                  config={RuleBuilderConfig}
                  type={`TREATMENTS.${subScenario.eventType}`}
                  systemType="newtrident"
                  ruleTypes={ruleTypes}
                  ruleTypesPassedFromSystem
                />
              </FormInputWithError>
            )}
            {_.isEmpty(subScenario.eventType) && (
              <span className="text-note">({i18n.t('targets_page.rules.placeholder')})</span>
            )}
          </Grid.Column>
        </Grid>
        <br />
      </Segment>
    );
  }
}
